import api from '../services/api';
import { Dispatch } from 'redux';
import { handleError } from './dashboard';
import {
  FiltersModel,
  PagingModel,
  TermOffer,
} from '../components/LeasePricing/UnifiedView/models';
import { format } from 'date-fns';
import { ALL } from '../constants';
import { generateLeaseKey } from './schema';

export const FLOORPLAN_ACTIONS = {
  FETCH_DATA: 'LP_FLOORPLAN_FETCH_DATA',
  CLEAR_DATA: 'LP_FLOORPLAN_CLEAR_DATA',
  SET_CURRENT_PAGE: 'LP_FLOORPLAN_SET_CURRENT_PAGE',
  SET_ELEMENTS_PER_PAGE: 'LP_FLOORPLAN_SET_ELEMENTS_PER_PAGE',
  APPROVE_OFFER: 'LP_FLOORPLAN_APPROVE_OFFER',
  EXCLUDE_OFFER: 'LP_FLOORPLAN_EXCLUDE_OFFER',
  RESET_OFFER: 'LP_FLOORPLAN_RESET_OFFER',
  SET_SHOW_APPROVE_ALL_CONFIRMATION: 'SET_SHOW_APPROVE_ALL_CONFIRMATION',
  APPROVE_ALL: 'LP_FLOORPLAN_APPROVE_ALL',
};

export const UNITS_ACTIONS = {
  FETCH_DATA: 'LP_UNITS_FETCH_DATA',
};

export const STATS_ACTIONS = {
  FETCH_DATA: 'LP_STATS_FETCH_DATA',
};

export const FILTERS_ACTIONS = {
  FETCH_DATA: 'LP_FILTERS_FETCH_DATA',
  SET_BED: 'LP_FILTERS_SET_BED',
  SET_PROPERTY_CODE: 'LP_FILTERS_SET_PROPERTY_CODE',
  SET_UNIT_TYPE: 'LP_FILTERS_SET_UNIT_TYPE',
  SET_FILTERS_OPTIONS: 'LP_FILTERS_SET_FILTERS_OPTIONS',
  SET_OFFER_STATE: 'LP_FILTERS_SET_OFFER_STATE',
  SET_UNIT_STATE: 'LP_FILTERS_SET_UNIT_STATE',
  SET_SORT_BY: 'LP_FILTERS_SET_SORT_BY',
  SET_STATS_PERIOD: 'LP_FILTERS_SET_STATS_PERIOD',
};

export const PRICE_MATRICE_MODAL_ACTIONS = {
  SET_DATA: 'LP_PM_MODAL_SET_DATA',
  FETCH_DATA: 'LP_PM_MODAL_FETCH_DATA',
  CLEAR_DATA: 'LP_PM_MODAL_CLEAR_DATA',
  SHIFT_ALL_OFFERS: 'LP_PM_MODAL_SHIFT_ALL_OFFERS',
  SET_SHIFT_OFFERS_MODAL: 'LP_PM_MODAL_SET_SHIFT_OFFERS_MODAL',
};
export const PRICE_RECOMMENDATION_MODAL_ACTIONS = {
  SET_DATA: 'LP_PR_MODAL_SET_DATA',
  CLEAR_DATA: 'LP_PR_MODAL_CLEAR_DATA',
  UPDATE_PRICE: 'LP_PR_MODAL_UPDATE_PRICE',
};
export const FLAG_ITEM_MODAL_ACTIONS = {
  SET_ITEM: 'LP_FLAG_ITEM_SET_ITEM',
  CLEAR_ITEM: 'LP_FLAG_ITEM_CLEAR_ITEM',
  FETCH_COMMENTS: 'LP_FLAG_ITEM_FETCH_COMMENTS',
  ADD_COMMENT: 'LP_FLAG_ITEM_ADD_COMMENT',
  RESOLVE_FLAG: 'LP_FLAG_ITEM_RESOLVE_FLAG',
};
export const MODULE_ACTIONS = {
  IS_LOADING: 'LP_IS_LOADING',
};

export const setLoading = (dispatch: Dispatch, isLoading: boolean) =>
  dispatch({
    type: MODULE_ACTIONS.IS_LOADING,
    payload: isLoading,
  });

export const getFloorplanData =
  (filters: FiltersModel, paging: PagingModel | undefined) =>
  (dispatch: Dispatch) => {
    setLoading(dispatch, true);
    api
      .getFloorplanData({ ...filters, ...paging })
      .then((res: any) => {
        dispatch({
          type: FLOORPLAN_ACTIONS.FETCH_DATA,
          payload: res.data,
        });
      })
      .catch((err) => {
        handleError(err, dispatch);
        console.error(err.message);
      })
      .finally(() => {
        setLoading(dispatch, false);
      });
  };

export const getFiltersOptions = () => (dispatch: Dispatch) => {
  Promise.all([api.getManagedProperties(), api.getUnitTypes()]).then(
    ([res1, res2]) => {
      dispatch({
        type: FILTERS_ACTIONS.SET_FILTERS_OPTIONS,
        payload: {
          properties: res1.data,
          unitTypes: res2.data,
        },
      });
    }
  );
};

export const clearFloorplanData = () => (dispatch: Dispatch) => {
  dispatch({
    type: FLOORPLAN_ACTIONS.CLEAR_DATA,
  });
};

export const getUnitData =
  (
    propertyCode: string,
    unitType: string,
    selectedFilters: FiltersModel,
    id: string
  ) =>
  (dispatch: Dispatch) => {
    setLoading(dispatch, true);
    api
      .getUnitsData({
        propertyCode,
        unitType,
        beds: selectedFilters.beds,
        offerStatus: selectedFilters.offerStatus,
        unitStatus: selectedFilters.unitStatus,
      })
      .then((res) => {
        dispatch({
          type: UNITS_ACTIONS.FETCH_DATA,
          payload: {
            id: id,
            data: res.data,
          },
        });
      })
      .catch((err) => {
        handleError(err, dispatch);
        console.error(err.message);
      })
      .finally(() => {
        setLoading(dispatch, false);
      });
  };

export const getStatsData = (filters: FiltersModel) => (dispatch: Dispatch) => {
  setLoading(dispatch, true);

  const startDate = format(new Date(), 'yyyy-MM-dd');

  api
    .getStatsData({
      startDate: startDate,
      length: filters.statsPeriod,
      bedRoom: filters.beds !== ALL ? filters.beds : null,
      propertyCode:
        filters.propertyCode?.id !== ALL ? filters.propertyCode?.id : null,
      unitType: filters.unitType !== ALL ? filters.unitType : null,
    })
    .then((res: any) => {
      dispatch({
        type: STATS_ACTIONS.FETCH_DATA,
        payload: {
          data: res.data,
          summaryLength: filters.statsPeriod,
        },
      });
    })
    .catch((err) => {
      handleError(err, dispatch);
      console.error(err.message);
    })
    .finally(() => {
      setLoading(dispatch, false);
    });
};

export const setBed = (selectedBed: string) => (dispatch: Dispatch) => {
  dispatch({
    type: FILTERS_ACTIONS.SET_BED,
    payload: selectedBed,
  });
};

export const setStatsPeriod = (statsPeriod: number) => (dispatch: Dispatch) => {
  dispatch({
    type: FILTERS_ACTIONS.SET_STATS_PERIOD,
    payload: statsPeriod,
  });
};
export const setPropertyCode =
  (selectedPropertyCode: { id: string; name: string }) =>
  (dispatch: Dispatch) => {
    dispatch({
      type: FILTERS_ACTIONS.SET_PROPERTY_CODE,
      payload: { id: selectedPropertyCode.id, name: selectedPropertyCode.name },
    });
  };

export const setUnitType =
  (selectedUnitType: string) => (dispatch: Dispatch) => {
    dispatch({
      type: FILTERS_ACTIONS.SET_UNIT_TYPE,
      payload: selectedUnitType,
    });
  };

export const setOfferState =
  (selectedOfferState: string | undefined) => (dispatch: Dispatch) => {
    dispatch({
      type: FILTERS_ACTIONS.SET_OFFER_STATE,
      payload: selectedOfferState,
    });
  };

export const setUnitState =
  (selectedUnitState: string | undefined) => (dispatch: Dispatch) => {
    dispatch({
      type: FILTERS_ACTIONS.SET_UNIT_STATE,
      payload: selectedUnitState,
    });
  };

export const setSortBy =
  (selectedSortBy: string | undefined) => (dispatch: Dispatch) => {
    dispatch({
      type: FILTERS_ACTIONS.SET_SORT_BY,
      payload: selectedSortBy,
    });
  };

export const setShowApproveAllConfirmation =
  (show: boolean) => (dispatch: Dispatch) => {
    dispatch({
      type: FLOORPLAN_ACTIONS.SET_SHOW_APPROVE_ALL_CONFIRMATION,
      payload: show,
    });
  };

export const setPriceMatricesModal =
  (data: {
    hasToFetch: boolean;
    unitTypeKey: string | undefined;
    propertyCode: string | undefined;
    unitType: string | undefined;
    unitCode: string | undefined;
    beds: string | undefined;
    sqft: string | undefined;
    name: string | undefined;
    unitDetailId?: string | undefined;
    isShiftOffersOpen?: boolean;
  }) =>
  (dispatch: Dispatch) => {
    setLoading(dispatch, true);

    if (data.hasToFetch) {
      api
        .getPriceMatrices2(data)
        .then((res: any) => {
          dispatch({
            type: PRICE_MATRICE_MODAL_ACTIONS.FETCH_DATA,
            payload: {
              propertyCode: data.propertyCode,
              unitTypeKey: data.unitTypeKey,
              unitDetailId: data.unitDetailId,
              unitCode: data.unitCode,
              unitType: data.unitType,
              beds: data.beds,
              sqft: data.sqft,
              name: data.name,
              data: res.data,
              isShiftOffersOpen: data.isShiftOffersOpen,
            },
          });
        })
        .catch((err) => {
          handleError(err, dispatch);
          console.error(err.message);
        })
        .finally(() => {
          setLoading(dispatch, false);
        });
    } else {
      dispatch({
        type: PRICE_MATRICE_MODAL_ACTIONS.SET_DATA,
        payload: {
          propertyCode: data.propertyCode,
          unitTypeKey: data.unitTypeKey,
          unitDetailId: data.unitDetailId,
          unitType: data.unitType,
          unitCode: data.unitCode,
          beds: data.beds,
          sqft: data.sqft,
          name: data.name,
          isShiftOffersOpen: data.isShiftOffersOpen,
        },
      });

      setLoading(dispatch, false);
    }
  };

export const clearPriceMatricesModal = () => (dispatch: Dispatch) => {
  dispatch({
    type: PRICE_MATRICE_MODAL_ACTIONS.CLEAR_DATA,
    payload: undefined,
  });
};

export const setPriceRecommendationModal =
  (data: { propertyCode: string; unitType: string; unitCode: string }) =>
  (dispatch: Dispatch) => {
    setLoading(dispatch, true);
    api
      .getOffersById()
      .then((res) => {
        let newData: {
          propertyCode: string;
          unitType: string;
          unitCode: string;
          termOffers?: Array<TermOffer>;
        } = { ...data };

        newData['termOffers'] = res as Array<TermOffer>;

        dispatch({
          type: PRICE_RECOMMENDATION_MODAL_ACTIONS.SET_DATA,
          payload: newData,
        });
      })
      .catch((err) => {
        handleError(err, dispatch);
        console.error(err.message);
      })
      .finally(() => {
        setLoading(dispatch, false);
      });
  };

export const clearPriceRecommendationModal = () => (dispatch: Dispatch) => {
  dispatch({
    type: PRICE_RECOMMENDATION_MODAL_ACTIONS.CLEAR_DATA,
    payload: undefined,
  });
};

export const setElementsPerPage =
  (elementsPerPage: number) => (dispatch: Dispatch) => {
    dispatch({
      type: FLOORPLAN_ACTIONS.SET_ELEMENTS_PER_PAGE,
      payload: elementsPerPage,
    });
  };

export const setCurrentPage = (currentPage: number) => (dispatch: Dispatch) => {
  dispatch({
    type: FLOORPLAN_ACTIONS.SET_CURRENT_PAGE,
    payload: currentPage,
  });
};

export const approveOffer =
  (propertyCode: string, unitType: string) => (dispatch: Dispatch) => {
    setLoading(dispatch, true);
    api
      .unifiedApproveOffer(propertyCode, unitType)
      .then((res) => {
        console.log(res);

        dispatch({
          type: FLOORPLAN_ACTIONS.APPROVE_OFFER,
          payload: { propertyCode, unitType },
        });

        dispatch({
          type: 'SHOW_NOTIFICATIONS',
          payload: {
            message: `Offer approved`,
            type: 'success',
          },
        });
      })
      .catch((err) => {
        handleError(err, dispatch);
        console.error(err.message);
      })
      .finally(() => {
        setLoading(dispatch, false);
      });
  };

export const excludeOffer =
  (propertyCode: string, unitType: string, isExcluded: boolean) =>
  (dispatch: Dispatch) => {
    setLoading(dispatch, true);
    api
      .unifiedExcludeOffer(propertyCode, unitType)
      .then((res) => {
        console.log(res);

        dispatch({
          type: FLOORPLAN_ACTIONS.EXCLUDE_OFFER,
          payload: { propertyCode, unitType },
        });
      })
      .catch((err) => {
        handleError(err, dispatch);
        console.error(err.message);
      })
      .finally(() => {
        setLoading(dispatch, false);
      });
  };

export const resetOffer =
  (propertyCode: string, unitType: string) => (dispatch: Dispatch) => {
    dispatch({
      type: FLOORPLAN_ACTIONS.RESET_OFFER,
      payload: { propertyCode, unitType },
    });
  };

export const updatePrice =
  (propertyCode: string, unitType: string, newPrice: string, term: string) =>
  (dispatch: Dispatch) => {
    dispatch({
      type: PRICE_RECOMMENDATION_MODAL_ACTIONS.UPDATE_PRICE,
      payload: {
        propertyCode: propertyCode,
        unitType: unitType,
        newPrice: newPrice,
        term: term,
      },
    });
  };

export const setFlagDetails =
  (
    id: string,
    propertyCode: string,
    unitType: string,
    user: string,
    isFlagged: boolean
  ) =>
  (dispatch: Dispatch) => {
    dispatch({
      type: FLAG_ITEM_MODAL_ACTIONS.SET_ITEM,
      payload: {
        id: id,
        type: 'Lease Price',
        info: {
          propertyCode: propertyCode,
          unitType: unitType,
        },
        resolved: !isFlagged,
        title: `${propertyCode} - ${unitType}`,
        userName: user,
        comments: null,
      },
    });
  };

export const getFlagDetails = (id: string) => (dispatch: Dispatch) => {
  api
    .getFlagDetails(id)
    .then((res: any) => {
      dispatch({
        type: FLAG_ITEM_MODAL_ACTIONS.SET_ITEM,
        payload: res.data,
      });
    })
    .catch((err) => {
      handleError(err, dispatch);
      console.error(err.message);
    })
    .finally(() => {});
};

export const clearFlagItem = () => (dispatch: Dispatch) => {
  dispatch({
    type: FLAG_ITEM_MODAL_ACTIONS.CLEAR_ITEM,
  });
};

export const shiftAllOffers = (data: any) => (dispatch: Dispatch) => {
  setLoading(dispatch, true);

  let leaseKey = generateLeaseKey(data.propertyCode, data.unitType);
  leaseKey = data.unitId ? `${leaseKey}-${data.unitId}` : leaseKey;

  api
    .shiftAllOffers(data)
    .then((res) => {
      const { success, ...matrixData } = res.data;

      dispatch({
        type: PRICE_MATRICE_MODAL_ACTIONS.SHIFT_ALL_OFFERS,
        payload: {
          leaseKey,
          propertyCode: data.propertyCode,
          unitType: data.unitType,
          unitId: data.unitId,
          shiftAmount: data.shiftAmount,
          shiftBy: data.shiftBy,
        },
      });

      let unitTypeKey = generateLeaseKey(data.propertyCode, data.unitType);

      unitTypeKey = data.unitId ? `${unitTypeKey}-${data.unitId}` : unitTypeKey;

      dispatch({
        type: PRICE_MATRICE_MODAL_ACTIONS.FETCH_DATA,
        payload: {
          propertyCode: data.propertyCode,
          unitTypeKey: unitTypeKey,
          unitDetailId: data.unitDetailId,
          unitType: data.unitType,
          unitCode: data.unitId,
          beds: data.beds,
          sqft: data.sqft,
          name: data.name,
          data: matrixData,
          isShiftOffersOpen: true,
        },
      });

      dispatch({
        type: 'SHOW_NOTIFICATIONS',
        payload: {
          message: `Offers updated`,
          type: 'success',
        },
      });
    })
    .catch((err) => {
      handleError(err, dispatch);
    })
    .finally(() => {
      setLoading(dispatch, false);
    });
};

export const setShiftOffersModal =
  (isOpen: boolean) => (dispatch: Dispatch) => {
    dispatch({
      type: PRICE_MATRICE_MODAL_ACTIONS.SET_SHIFT_OFFERS_MODAL,
      payload: isOpen,
    });
  };

export const addFlagComment =
  (id: string, propertyCode: string, unitType: string, comment: string) =>
  (dispatch: Dispatch) => {
    setLoading(dispatch, true);
    api
      .addFlagComment({ id, propertyCode, unitType, comment })
      .then((res) => {
        dispatch({
          type: FLAG_ITEM_MODAL_ACTIONS.ADD_COMMENT,
          payload: res.data,
        });

        dispatch({
          type: 'SHOW_NOTIFICATIONS',
          payload: {
            message: `Comment added`,
            type: 'success',
          },
        });
      })
      .catch((err) => {
        handleError(err, dispatch);
        console.error(err.message);
      })
      .finally(() => {
        setLoading(dispatch, false);
      });
  };

export const resolveFlagItem = (id: string) => (dispatch: Dispatch) => {
  setLoading(dispatch, true);
  api
    .resolveFlagItem(id)
    .then((res) => {
      dispatch({
        type: FLAG_ITEM_MODAL_ACTIONS.ADD_COMMENT,
        payload: res.data,
      });
      dispatch({
        type: FLAG_ITEM_MODAL_ACTIONS.RESOLVE_FLAG,
        payload: res.data,
      });
      dispatch({
        type: 'SHOW_NOTIFICATIONS',
        payload: {
          message: `Flag resolved`,
          type: 'success',
        },
      });
    })
    .catch((err) => {
      handleError(err, dispatch);
      console.error(err.message);
    })
    .finally(() => {
      setLoading(dispatch, false);
    });
};

export const approveAll =
  (data: any, filters: FiltersModel, paging: PagingModel | undefined) =>
  (dispatch: Dispatch) => {
    setLoading(dispatch, true);

    api
      .approveAll(data)
      .then((res: any) => {
        dispatch(setCurrentPage((paging?.page || 0) - 1) as any);
        dispatch({
          type: 'SHOW_NOTIFICATIONS',
          payload: {
            message: data.approve ? `Offers approved` : `Offers un-approved`,
            type: 'success',
          },
        });
      })
      .catch((err) => {
        handleError(err, dispatch);
        console.error(err.message);
      })
      .finally(() => {
        setLoading(dispatch, false);
      });
  };
